// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  SESSION_TOKEN_LOCAL_STORAGE_NAME as LEARNER_SESSION_TOKEN_LOCAL_STORAGE_NAME,
  OAUTH2_REFRESH_TOKEN,
  OAUTH2_SESSION_TOKEN,
  PARENT_TRANSFER_TOKEN_COOKIE_NAME
} from "@outschool/auth-shared";
import * as Auth from "@outschool/auth-shared";
import { LOGGED_IN_USER_EXP_COOKIE_NAME } from "@outschool/experiments-shared";
import { isLocalStorageSupported } from "@outschool/local-storage";
import { websiteHost } from "@outschool/routes";
import {
  EsaSessionData,
  destroyEsaSession,
  getEsaSession,
  isEsaSessionPresent
} from "@outschool/ui-auth";
import Cookies from "js-cookie";
import * as _ from "lodash";
import React from "react";

import { isDevelopment, isTest } from "../../shared/Env";

// This cookie is stored for ease of adding to our analytics tracking. It
// SHOULD NOT be referenced for determining whether or not the user is impersonating
// to do that one should look at the `isImpersonating` value on the session token.
const IS_IMPERSONATING_COOKIE_NAME = "isImpersonating";
const SESSION_TOKEN_LOCAL_STORAGE_NAME = "jwt";
export const REFRESH_TOKEN_LOCAL_STORAGE_NAME = "jwtRefreshToken";
let localStorage = isLocalStorageSupported();
export const __testOnly_setLocalStorage = (ls: any) => {
  localStorage = ls;
};

function updateLoggedInUserExperimentCookie(loggedInUserExpUid?: string) {
  if (!loggedInUserExpUid) {
    Cookies.remove(LOGGED_IN_USER_EXP_COOKIE_NAME);
  } else {
    Cookies.set(LOGGED_IN_USER_EXP_COOKIE_NAME, loggedInUserExpUid, {
      secure: !(isTest || isDevelopment),
      sameSite: "lax"
    });
  }
}

function updateIsImpersonatingCookie(isImpersonating?: boolean) {
  if (!!isImpersonating) {
    Cookies.set(IS_IMPERSONATING_COOKIE_NAME, "true", {
      secure: !(isTest || isDevelopment),
      sameSite: "lax",
      domain: new URL(websiteHost()).hostname
    });
  } else {
    Cookies.remove(IS_IMPERSONATING_COOKIE_NAME);
  }
}

type AuthData = {
  uid: string | null;
  isAdmin: boolean;
  isLeader: boolean;
  isLoggedIn: boolean;
  hasFacebook: boolean;
  hasGoogle: boolean;
  hasApple: boolean;
  hasPassword: boolean;
  hasAccount: boolean;
  roles: Auth.Roles[];
};

const DEFAULT_AUTH_DATA: AuthData = {
  uid: null,
  isAdmin: false,
  isLeader: false,
  isLoggedIn: false,
  hasFacebook: false,
  hasGoogle: false,
  hasApple: false,
  hasPassword: false,
  hasAccount: false,
  roles: []
};

type Context = {
  getTokens(): {
    sessionToken: string | null;
    refreshToken: string | null;
    decodedToken: Auth.Auth | null;
  };
  setTokens(sessionToken: string, refreshToken?: string): void;
  resetTokens(): void;
  authData: AuthData;
  getAuthData(): AuthData;
  esaSessionData: EsaSessionData;
  isEsaSession: boolean;
  destroyEsaSession(): void;
};

const TokenContext = React.createContext<Context>({
  getTokens: () => ({
    sessionToken: null,
    decodedToken: null,
    refreshToken: null,
    esaToken: null
  }),
  setTokens: (_s: string | null) => null,
  resetTokens: () => null,
  authData: { ...DEFAULT_AUTH_DATA },
  getAuthData: () => ({ ...DEFAULT_AUTH_DATA }),
  esaSessionData: {},
  isEsaSession: false,
  destroyEsaSession: () => {}
});

function setTokensInLocalStorage(
  sessionToken: string | null,
  refreshToken: string | null
) {
  if (localStorage) {
    if (!!sessionToken) {
      localStorage.setItem(SESSION_TOKEN_LOCAL_STORAGE_NAME, sessionToken);
    } else {
      localStorage.removeItem(SESSION_TOKEN_LOCAL_STORAGE_NAME);
    }
    if (!!refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_NAME, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_NAME);
    }
  }
}

export const TokenProvider: React.FC = ({ children }) => {
  let initialTokenStr: string | null = null;
  let refreshTokenStr: string | null = null;

  const oauth2SessionToken = Cookies.get(OAUTH2_SESSION_TOKEN);
  const oauth2RefreshToken = Cookies.get(OAUTH2_REFRESH_TOKEN);
  const switchUserToken = Cookies.get("switchUserToken");
  const parentTransferToken = Cookies.get(PARENT_TRANSFER_TOKEN_COOKIE_NAME);
  if (oauth2SessionToken) {
    initialTokenStr = oauth2SessionToken;
    Cookies.remove(OAUTH2_SESSION_TOKEN);
    refreshTokenStr = oauth2RefreshToken;
    Cookies.remove(OAUTH2_REFRESH_TOKEN);
  } else if (switchUserToken) {
    initialTokenStr = switchUserToken;
    Cookies.remove("switchUserToken");
  } else if (parentTransferToken) {
    if (localStorage) {
      localStorage.removeItem(LEARNER_SESSION_TOKEN_LOCAL_STORAGE_NAME);
    }
    initialTokenStr = parentTransferToken;
    Cookies.remove(PARENT_TRANSFER_TOKEN_COOKIE_NAME);
  } else if (localStorage) {
    try {
      initialTokenStr = localStorage.getItem(SESSION_TOKEN_LOCAL_STORAGE_NAME);
      refreshTokenStr = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE_NAME);
    } catch (e) {
      console.log(e);
    }
  }
  // Make sure that localstorage matches the tokens we're using in mem
  setTokensInLocalStorage(initialTokenStr, refreshTokenStr);

  const sessionToken = React.useRef<string | null>(initialTokenStr ?? null);
  const decodedToken = React.useRef<Auth.Auth | null>(
    Auth.decodeToken(initialTokenStr) as Auth.Auth
  );
  const refreshToken = React.useRef<string | null>(refreshTokenStr ?? null);

  const [isEsaSession, setIsEsaSession] = React.useState(isEsaSessionPresent());
  const [esaSessionData, setEsaSessionData] = React.useState(
    getEsaSession() || {}
  );

  const destroyEsaSessionAndRefresh = React.useCallback(() => {
    destroyEsaSession();
    setIsEsaSession(false);
    setEsaSessionData({});
  }, []);

  const getAuthData = React.useCallback(() => {
    // Use the refresh token for decoded values (longer-lived / won't be expired)
    const _decodedToken = !Auth.hasExpired(decodedToken.current)
      ? decodedToken.current
      : (Auth.decodeToken(refreshToken.current) as Auth.Auth);

    updateIsImpersonatingCookie(_decodedToken?.isImpersonating);
    updateLoggedInUserExperimentCookie(_decodedToken?.uid);

    if (!_decodedToken) {
      // Only remove token if there was a previous session that we can't refresh
      if (!!decodedToken.current && isEsaSessionPresent()) {
        destroyEsaSessionAndRefresh();
      }

      return { ...DEFAULT_AUTH_DATA };
    }
    const isLoggedIn = Auth.isLoggedIn(_decodedToken);

    return {
      uid: _decodedToken?.uid ?? null,
      isLoggedIn,
      isAdmin: isLoggedIn && Auth.isAdmin(_decodedToken),
      isLeader: isLoggedIn && Auth.isLeader(_decodedToken),
      adminForOrganizationType:
        isLoggedIn && _decodedToken?.adminForOrganizationType,
      hasFacebook: Auth.hasFacebook(_decodedToken),
      hasGoogle: Auth.hasGoogle(_decodedToken),
      hasApple: Auth.hasApple(_decodedToken),
      hasPassword: Auth.hasPassword(_decodedToken),
      hasAccount: Auth.hasAccount(_decodedToken),
      roles: Auth.getRoles(_decodedToken)
    };
  }, [destroyEsaSessionAndRefresh]);

  const [authData, setAuthData] = React.useState<AuthData>(getAuthData());

  const getTokens = React.useCallback(() => {
    return {
      sessionToken: sessionToken.current,
      decodedToken: decodedToken.current,
      refreshToken: refreshToken.current
    };
  }, []);

  const setTokens = React.useCallback(
    (_sessionToken: string | null, _refreshToken: string | null) => {
      if (_sessionToken) {
        const oldDecodedToken = decodedToken.current;
        const newDecodedToken = Auth.decodeToken(_sessionToken) as Auth.Auth;
        sessionToken.current = _sessionToken;
        decodedToken.current = newDecodedToken;
        updateIsImpersonatingCookie(newDecodedToken?.isImpersonating);
        updateLoggedInUserExperimentCookie(newDecodedToken?.uid);
        refreshToken.current = _refreshToken ?? null;

        setTokensInLocalStorage(_sessionToken, _refreshToken);
        if (
          !_.isEqual(
            _.omit(newDecodedToken, ["iat", "exp"]),
            _.omit(oldDecodedToken, ["iat", "exp"])
          )
        ) {
          setAuthData(getAuthData());
        }
      }
    },
    [getAuthData]
  );

  const resetTokens = React.useCallback(() => {
    sessionToken.current = null;
    refreshToken.current = null;
    decodedToken.current = null;
    if (localStorage) {
      localStorage.removeItem(SESSION_TOKEN_LOCAL_STORAGE_NAME);
      localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_NAME);
    }
    Cookies.remove("sessionToken", { path: "/users/connect/" });
    destroyEsaSessionAndRefresh();
    updateLoggedInUserExperimentCookie();
    updateIsImpersonatingCookie();
    setAuthData({ ...DEFAULT_AUTH_DATA });
  }, [destroyEsaSessionAndRefresh]);

  const providerValues = React.useMemo(
    () => ({
      getTokens,
      setTokens,
      resetTokens,
      authData,
      getAuthData,
      esaSessionData,
      isEsaSession,
      destroyEsaSession: destroyEsaSessionAndRefresh
    }),
    [
      getTokens,
      setTokens,
      resetTokens,
      authData,
      getAuthData,
      esaSessionData,
      isEsaSession,
      destroyEsaSessionAndRefresh
    ]
  );

  return (
    <TokenContext.Provider value={providerValues}>
      {children}
    </TokenContext.Provider>
  );
};

export const useTokenContext = () => {
  const tokenContext = React.useContext(TokenContext);
  if (!tokenContext) {
    throw new Error("Called useTokenContext outside of a TokenProvider");
  }
  return tokenContext;
};

export function withTokenContext(DecoratedComponent) {
  const displayName = DecoratedComponent.displayName || "Component";

  return class TokenContextWrapper extends React.Component {
    static displayName = `TokenContextWrapper(${displayName})`;

    render() {
      return (
        <TokenContext.Consumer>
          {context => <DecoratedComponent {...context} {...this.props} />}
        </TokenContext.Consumer>
      );
    }
  };
}

export function useCurrentUserHasRole(role: Auth.Roles) {
  const { getTokens } = useTokenContext();
  return Auth.hasRole(getTokens().decodedToken, role);
}

export function useIsImpersonating() {
  const { getTokens } = useTokenContext();
  return !!getTokens()?.decodedToken?.isImpersonating;
}
